import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { PageHeader } from "@/components/AppShell";
import { fmtEur, fmtKwh, STATUS_LABELS, STATUS_COLORS } from "@/lib/format";
import { ArrowLeft, MapPinned, Mail, Phone, FileText, Sparkles, UploadCloud, Trash2, Wand2, Activity } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const billRef = useRef();

  const reload = () => {
    api.get(`/customers/${id}/related`).then((r) => setData(r.data));
    api.get(`/customers/${id}/timeline`).then((r) => setTimeline(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [id]);

  const onDocUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Max 4MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/customers/${id}/documents`, { name: f.name, type: "document", mime: f.type, data_base64: reader.result });
        toast.success("Documento caricato");
        reload();
      } catch (err) { toast.error(formatErr(err)); }
    };
    reader.readAsDataURL(f);
  };

  const aiAnalyzeBill = async () => {
    if (!billRef.current) return;
    billRef.current.click();
  };

  const onBillFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Carica un'immagine (foto della bolletta)"); return; }
    if (f.size > 4 * 1024 * 1024) { toast.error("Max 4MB"); return; }
    setAiBusy(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const { data: r } = await api.post("/ai/analyze-bill", { image_base64: reader.result });
          const ext = r?.data || {};
          // Update customer with extracted fields
          const upd = {};
          if (ext.annual_consumption_kwh) upd.annual_consumption_kwh = parseFloat(ext.annual_consumption_kwh);
          if (ext.energy_price_per_kwh) upd.energy_price_per_kwh = parseFloat(ext.energy_price_per_kwh);
          if (ext.meter_power_kw) upd.meter_power_kw = parseFloat(ext.meter_power_kw);
          if (Object.keys(upd).length) {
            await api.patch(`/customers/${id}`, { ...data.customer, ...upd });
            toast.success(`Estratti: ${Object.keys(upd).join(", ")}`);
          } else toast.message(`AI: ${JSON.stringify(ext).slice(0,200)}`);
          // Save bill as document
          await api.post(`/customers/${id}/documents`, { name: f.name, type: "bolletta", mime: f.type, data_base64: reader.result });
          reload();
        } catch (err) { toast.error(formatErr(err)); }
        finally { setAiBusy(false); }
      };
      reader.readAsDataURL(f);
    } catch { setAiBusy(false); }
  };

  const delDoc = async (did) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/documents/${did}`); reload();
  };

  if (!data) return <div className="p-8 text-zinc-500">Caricamento…</div>;
  const c = data.customer;
  const name = c.business_name || `${c.first_name} ${c.last_name}`.trim();

  return (
    <div data-testid="page-customer-detail">
      <PageHeader eyebrow="Cliente" title={name} subtitle={`${STATUS_LABELS[c.status] || c.status} · ${c.city || ""}`} actions={
        <Link to="/app/customers" className="px-3 py-2 border border-zinc-200/70 rounded-lg text-sm flex items-center gap-1.5"><ArrowLeft size={13}/> Indietro</Link>
      }/>
      <div className="px-8 py-6 grid lg:grid-cols-3 gap-5">
        <div className="space-y-5">
          <div className="bg-white border border-zinc-200/70 rounded-2xl p-5">
            <div className="font-semibold mb-3">Dati</div>
            <div className="space-y-2 text-sm">
              {c.email && <div className="flex items-center gap-2 text-zinc-900"><Mail size={13}/>{c.email}</div>}
              {c.phone && <div className="flex items-center gap-2 text-zinc-900"><Phone size={13}/>{c.phone}</div>}
              {c.address && <div className="flex items-center gap-2 text-zinc-900"><MapPinned size={13}/>{c.address}, {c.city}</div>}
              <div className="pt-3 mt-3 border-t border-zinc-200/70 grid grid-cols-2 gap-2 text-xs">
                <div><div className="text-zinc-500">Consumo annuo</div><div className="font-mono text-amber-600">{fmtKwh(c.annual_consumption_kwh)}</div></div>
                <div><div className="text-zinc-500">Prezzo €/kWh</div><div className="font-mono">€ {c.energy_price_per_kwh}</div></div>
                <div><div className="text-zinc-500">Potenza contatore</div><div className="font-mono">{c.meter_power_kw} kW</div></div>
                <div><div className="text-zinc-500">Interesse batteria</div><div className="font-mono">{c.interest_battery ? "Sì" : "No"}</div></div>
              </div>
            </div>
            <Link to={`/app/simulator?customer=${id}`} className="mt-4 w-full bg-zinc-900 text-white font-semibold rounded-xl py-2.5 flex items-center justify-center gap-2 text-sm" data-testid="open-simulator-btn">
              <Sparkles size={14}/> Apri simulatore
            </Link>
            {!(c.latitude && c.longitude) && (
              <p className="text-[11px] text-zinc-500 mt-2">Suggerimento: inserisci Lat/Lng nel modulo cliente oppure cerca l’indirizzo nella barra del simulatore per centrare la mappa.</p>
            )}
          </div>

          <div className="bg-white border border-zinc-200/70 rounded-2xl p-5">
            <div className="font-semibold mb-3 flex items-center gap-2"><Wand2 size={16} className="text-emerald-600"/> AI: analizza bolletta</div>
            <p className="text-xs text-zinc-500 mb-3">Carica una foto della bolletta. L'AI estrae consumo annuo, prezzo €/kWh, potenza contatore e popola i campi cliente.</p>
            <input ref={billRef} type="file" accept="image/*" onChange={onBillFile} className="hidden" data-testid="bill-input"/>
            <button onClick={aiAnalyzeBill} disabled={aiBusy} className="w-full bg-zinc-900 text-white font-semibold rounded-xl py-2.5 flex items-center justify-center gap-2 text-sm disabled:opacity-50" data-testid="ai-bill-btn">
              {aiBusy ? "Analisi AI in corso…" : <><UploadCloud size={14}/> Carica foto bolletta</>}
            </button>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-zinc-200/70 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2"><MapPinned size={16} className="text-sky-600"/> Simulazioni ({data.simulations.length})</div>
            </div>
            {data.simulations.length === 0 && <div className="text-zinc-500 text-sm">Nessuna simulazione</div>}
            <div className="space-y-2">
              {data.simulations.map((s) => (
                <div key={s.id} className="bg-zinc-50 border border-zinc-200/70 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <div className="font-medium text-sm">{s.name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{s.created_at?.slice(0,10)}</div>
                  </div>
                  {s.results && (
                    <div className="text-right">
                      <div className="font-mono text-amber-600">{s.results.kwp} kWp · {s.results.panels_count} pan.</div>
                      <div className="font-mono text-xs text-emerald-600">{fmtEur(s.results.annual_saving_eur)}/anno</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-zinc-200/70 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2"><FileText size={16} className="text-amber-600"/> Preventivi ({data.quotes.length})</div>
            </div>
            {data.quotes.length === 0 && <div className="text-zinc-500 text-sm">Nessun preventivo</div>}
            <div className="space-y-2">
              {data.quotes.map((q) => (
                <Link to={`/app/quotes/${q.id}`} key={q.id} className="block bg-zinc-50 border border-zinc-200/70 rounded-lg p-3 flex items-center justify-between hover:border-zinc-300">
                  <div>
                    <div className="font-mono text-xs text-amber-600">{q.quote_number}</div>
                    <div className="text-xs text-zinc-500">{q.created_at?.slice(0,10)} · {q.items.length} voci</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-amber-600 text-lg">{fmtEur(q.total)}</div>
                    <div className="text-[10px] uppercase font-mono" style={{ color: STATUS_COLORS[q.status] || "#A1A1AA" }}>{q.status}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className="bg-white border border-zinc-200/70 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2"><Activity size={16} className="text-sky-600"/> Timeline attività ({timeline.length})</div>
            </div>
            {timeline.length === 0 && <div className="text-zinc-500 text-sm">Nessun evento</div>}
            <div className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin">
              {timeline.map((e, i) => (
                <div key={i} className="flex items-start gap-3 text-xs border-l-2 border-amber-300 pl-3 py-1">
                  <div className="flex-1">
                    <div className={`text-sm ${e.type === "quote_accepted" ? "text-emerald-600 font-semibold" : "text-zinc-900"}`}>{e.title}</div>
                    <div className="font-mono text-[10px] text-zinc-500">{e.at?.replace("T", " ").slice(0,16)} · {e.type}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-zinc-200/70 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold flex items-center gap-2"><UploadCloud size={16} className="text-amber-600"/> Documenti ({data.documents?.length || 0})</div>
              <label className="px-3 py-1.5 bg-zinc-900 text-white text-xs rounded-lg cursor-pointer font-semibold" data-testid="upload-doc-label">
                + Carica
                <input type="file" accept="image/*,application/pdf" onChange={onDocUpload} className="hidden" data-testid="doc-input"/>
              </label>
            </div>
            {(data.documents || []).length === 0 && <div className="text-zinc-500 text-sm">Nessun documento</div>}
            <div className="space-y-2">
              {(data.documents || []).map((d) => (
                <div key={d.id} className="bg-zinc-50 border border-zinc-200/70 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.name}</div>
                    <div className="text-xs text-zinc-500 font-mono uppercase">{d.type}</div>
                  </div>
                  <button onClick={() => delDoc(d.id)} className="text-rose-400 hover:bg-zinc-100 rounded p-1.5"><Trash2 size={13}/></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
